import { useEffect, useState, useCallback } from "react";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { PrivateAxios } from "../../environment/AxiosInstance";
import { toast } from "react-toastify";
import { CurrencyList } from '../../utils/StaticData';
import { UserAuth } from '../auth/Auth';

import * as XLSX from 'xlsx';
import '@progress/kendo-theme-default/dist/all.css';
import './SalesTargets.css';

//
// Phase 3 updates:
//   Issue 6  - Reasonableness confirm: prompt when revenue inputs look
//              suspiciously small (e.g. 1 rupee). Confirmed save still
//              proceeds, so it is a warning not a block.
//   Issue 15 - Full RNC/REC column labels with tooltips on hover.
//
const MIN_REASONABLE_AMOUNT = 1000;

export default function TargetModal({ target, onClose }) {
  const { MatchPermission, User } = UserAuth();

  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFieldChange = useCallback((id, field, value) => {
    setAssignees(prev => prev.map(a =>
      a.id === id ? { ...a, [field]: value !== null && value !== undefined ? value : 0 } : a
    ));
  }, []);

  const AssignedAmountCell = useCallback((props) => {
    const { dataItem } = props;
    const targetType = (target && target.target_type) || 'amount';

    if (targetType === 'number') {
      if (dataItem.inEdit) {
        return (
          <td>
            <NumericTextBox
              value={dataItem.tempAssignedNumber}
              onChange={(e) => handleFieldChange(dataItem.id, 'tempAssignedNumber', e.value)}
              format="n0"
              min={0}
              style={{ width: '100%' }}
            />
          </td>
        );
      }
      return (
        <td className="text-end">
          {new Intl.NumberFormat('en-IN').format(dataItem.assigned_number || 0)}
        </td>
      );
    } else {
      if (dataItem.inEdit) {
        return (
          <td>
            <NumericTextBox
              value={dataItem.tempAssignedAmount}
              onChange={(e) => handleFieldChange(dataItem.id, 'tempAssignedAmount', e.value)}
              format="n0"
              min={0}
              style={{ width: '100%' }}
            />
          </td>
        );
      }
      const amount = dataItem.assigned_amount || 0;
      const currencyCode = (target && target.currency) || 'INR';
      const currencySymbol = (CurrencyList.find(c => c.code === currencyCode) || {}).symbol || '₹';
      return (
        <td className="text-end">
          {amount ? `${currencySymbol}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount)}` : '-'}
        </td>
      );
    }
  }, [target, handleFieldChange]);

  const RNCCell = useCallback((props) => {
    const { dataItem } = props;
    if (dataItem.inEdit) {
      const currencyCode = (target && target.currency) || 'INR';
      return (
        <td>
          <NumericTextBox
            value={dataItem.tempRNC}
            onChange={(e) => handleFieldChange(dataItem.id, 'tempRNC', e.value)}
            format="n0"
            min={0}
            style={{ width: '100%' }}
            placeholder={`Amount in ${currencyCode}`}
          />
        </td>
      );
    }
    return <td className="text-end">{dataItem.rnc_ach || 0}</td>;
  }, [target, handleFieldChange]);

  const RECCell = useCallback((props) => {
    const { dataItem } = props;
    if (dataItem.inEdit) {
      const currencyCode = (target && target.currency) || 'INR';
      return (
        <td>
          <NumericTextBox
            value={dataItem.tempREC}
            onChange={(e) => handleFieldChange(dataItem.id, 'tempREC', e.value)}
            format="n0"
            min={0}
            style={{ width: '100%' }}
            placeholder={`Amount in ${currencyCode}`}
          />
        </td>
      );
    }
    return <td className="text-end">{dataItem.rec_ach || 0}</td>;
  }, [target, handleFieldChange]);

  const BalanceCell = useCallback((props) => {
    const { dataItem } = props;
    const targetType = (target && target.target_type) || 'amount';
    const achievementTotal = (Number(dataItem.rnc_ach) || 0) + (Number(dataItem.rec_ach) || 0);
    let assignedValue;
    if (targetType === 'number') {
      assignedValue = Number(dataItem.assigned_number) || 0;
    } else {
      assignedValue = Number(dataItem.assigned_amount) || 0;
    }
    const balance = achievementTotal - assignedValue;
    const percentage = assignedValue > 0 ? ((achievementTotal / assignedValue) * 100) : 0;
    const deficitPercentage = percentage - 100;
    const currencyCode = (target && target.currency) || 'INR';
    const currencySymbol = (CurrencyList.find(c => c.code === currencyCode) || {}).symbol || '₹';
    const balanceColor = balance >= 0 ? 'text-success' : 'text-danger';
    const percentageColor = deficitPercentage >= 0 ? 'text-success' : 'text-danger';

    return (
      <td className="text-end">
        <div>
          <div className={`fw-bold ${balanceColor}`}>
            {targetType === 'number'
              ? new Intl.NumberFormat('en-IN').format(Math.abs(balance))
              : `${currencySymbol}${new Intl.NumberFormat('en-IN').format(Math.abs(balance))}`}
          </div>
          <div className={`small ${percentageColor}`}>
            {deficitPercentage.toFixed(1)}%
          </div>
        </div>
      </td>
    );
  }, [target]);

  useEffect(() => {
    if (target && MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Doer Edit Sales'])) {
      fetchAssignees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!MatchPermission(['Sales All', 'View Sales', 'Edit Sales', 'Doer Edit Sales'])) {
    return null;
  }

  function fetchAssignees() {
    setLoading(true);
    PrivateAxios.get(`/sales-targets/${target.id}`)
      .then(res => {
        let assigneesData = [];
        if (res && res.data && res.data.data && res.data.data.assignees) assigneesData = res.data.data.assignees;
        else if (res && res.data && res.data.assignees) assigneesData = res.data.assignees;
        else if (Array.isArray(res && res.data && res.data.data)) assigneesData = res.data.data;
        else if (Array.isArray(res && res.data)) assigneesData = res.data;
        if (!Array.isArray(assigneesData)) assigneesData = [];
        const data = assigneesData.map(a => ({
          ...a,
          inEdit: false,
          tempRNC: a.rnc_ach || 0,
          tempREC: a.rec_ach || 0,
          tempAssignedAmount: a.assigned_amount || 0,
          tempAssignedNumber: a.assigned_number || 0
        }));
        setAssignees(data);
      })
      .catch((err) => {
        console.error("Fetch assignees error:", err);
        toast.error("Failed to load assignees");
        setAssignees([]);
      })
      .finally(() => setLoading(false));
  }

  async function updateAssigneeAchievement(assigneeId, payload) {
    if (!MatchPermission(['Sales All', 'Edit Sales', 'Doer Edit Sales'])) {
      toast.error("You don't have permission to update achievements");
      return;
    }
    try {
      await PrivateAxios.put(`/assignee-targets/${assigneeId}`, payload);
      toast.success("Assignee updated successfully");
      setAssignees(prev =>
        prev.map(a => a.id === assigneeId ? {
          ...a,
          rnc_ach: payload.RNC_Ach !== undefined ? payload.RNC_Ach : a.rnc_ach,
          rec_ach: payload.REC_Ach !== undefined ? payload.REC_Ach : a.rec_ach,
          assigned_amount: payload.assigned_amount !== undefined ? payload.assigned_amount : a.assigned_amount,
          assigned_number: payload.assigned_number !== undefined ? payload.assigned_number : a.assigned_number,
          tempRNC: payload.RNC_Ach !== undefined ? payload.RNC_Ach : a.tempRNC,
          tempREC: payload.REC_Ach !== undefined ? payload.REC_Ach : a.tempREC,
          tempAssignedAmount: payload.assigned_amount !== undefined ? payload.assigned_amount : a.tempAssignedAmount,
          tempAssignedNumber: payload.assigned_number !== undefined ? payload.assigned_number : a.tempAssignedNumber,
          inEdit: false
        } : a)
      );
    } catch (err) {
      const msg = (err && err.response && err.response.data && (err.response.data.error || err.response.data.message)) || 'Failed to update assignee';
      toast.error(msg);
    }
  }

  function handleEdit(id) {
    setAssignees(prev => prev.map(a =>
      a.id === id ? {
        ...a,
        inEdit: true,
        tempRNC: a.rnc_ach || 0,
        tempREC: a.rec_ach || 0,
        tempAssignedAmount: a.assigned_amount || 0,
        tempAssignedNumber: a.assigned_number || 0
      } : { ...a, inEdit: false }
    ));
  }

  function handleCancel(id) {
    setAssignees(prev => prev.map(a =>
      a.id === id ? {
        ...a,
        inEdit: false,
        tempRNC: a.rnc_ach || 0,
        tempREC: a.rec_ach || 0,
        tempAssignedAmount: a.assigned_amount || 0,
        tempAssignedNumber: a.assigned_number || 0
      } : a
    ));
  }

  function handleSave(assignee) {
    const targetType = (target && target.target_type) || 'amount';
    const rnc = Number(assignee.tempRNC) || 0;
    const rec = Number(assignee.tempREC) || 0;

    // Phase 3 (Issue 6): warn-but-allow on suspiciously small REVENUE values.
    if (targetType !== 'number') {
      const currencyCode = (target && target.currency) || 'INR';
      const flagged = [];
      if (rnc > 0 && rnc < MIN_REASONABLE_AMOUNT) flagged.push(`RNC (${rnc} ${currencyCode})`);
      if (rec > 0 && rec < MIN_REASONABLE_AMOUNT) flagged.push(`REC (${rec} ${currencyCode})`);
      if (flagged.length > 0) {
        const ok = window.confirm(
          `These values look unusually small: ${flagged.join(', ')}.\n\nWas the value entered in the correct unit? Save anyway?`
        );
        if (!ok) return;
      }
    }

    if (targetType === 'number') {
      updateAssigneeAchievement(assignee.id, {
        RNC_Ach: rnc,
        REC_Ach: rec,
        assigned_number: assignee.tempAssignedNumber || 0
      });
    } else {
      updateAssigneeAchievement(assignee.id, {
        RNC_Ach: rnc,
        REC_Ach: rec,
        assigned_amount: assignee.tempAssignedAmount || 0
      });
    }
  }

  const UserNameCell = (props) => {
    const { dataItem } = props;
    let userName = 'N/A';
    if (dataItem.assignee && dataItem.assignee.name) userName = dataItem.assignee.name;
    else if (dataItem.user && dataItem.user.name) userName = dataItem.user.name;
    else if (dataItem.assignee_name) userName = dataItem.assignee_name;
    else if (dataItem.name) userName = dataItem.name;
    else if (dataItem.assignee_id && User) {
      const found = User.find(u => u.id === dataItem.assignee_id);
      if (found) userName = found.name;
    }
    return <td>{userName}</td>;
  };

  const TotalCell = (props) => {
    const { dataItem } = props;
    const total = dataItem.inEdit
      ? (Number(dataItem.tempRNC) || 0) + (Number(dataItem.tempREC) || 0)
      : (Number(dataItem.rnc_ach) || 0) + (Number(dataItem.rec_ach) || 0);
    return <td className="text-end fw-bold">{new Intl.NumberFormat('en-IN').format(total)}</td>;
  };

  const ActionCell = (props) => {
    const { dataItem } = props;
    if (dataItem.inEdit) {
      return (
        <td>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-success" onClick={() => handleSave(dataItem)} title="Save">
              <i className="fas fa-check"></i>
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => handleCancel(dataItem.id)} title="Cancel">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </td>
      );
    }
    return (
      <td>
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-primary" onClick={() => handleEdit(dataItem.id)} title="Edit">
            <i className="fas fa-edit"></i>
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => deleteAssignee(dataItem.id)} title="Delete">
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </td>
    );
  };

  async function deleteAssignee(assigneeId) {
    if (window.confirm('Are you sure you want to delete this assignee?')) {
      try {
        await PrivateAxios.delete(`/assignee-targets/${assigneeId}`);
        toast.success('Assignee deleted successfully');
        setAssignees(prev => prev.filter(a => a.id !== assigneeId));
      } catch (err) {
        console.error('Delete assignee error:', err);
        toast.error('Failed to delete assignee');
      }
    }
  }

  const safeAssignees = Array.isArray(assignees) ? assignees : [];

  function calculateTotalAssigned() {
    const targetType = (target && target.target_type) || 'amount';
    if (targetType === 'number') {
      return safeAssignees.reduce((t, a) => t + (Number(a.assigned_number) || 0), 0);
    }
    return safeAssignees.reduce((t, a) => t + (Number(a.assigned_amount) || 0), 0);
  }

  function downloadExcel() {
    const targetType = (target && target.target_type) || 'amount';
    const headers = [
      'User Name',
      targetType === 'number' ? 'Assigned Number' : 'Assigned Amount',
      'RNC - Revenue From New Client',
      'REC - Revenue From Existing Client',
      'Total Achievement',
      'Balance',
      'Deficit %'
    ];
    const excelData = safeAssignees.map(a => {
      const achTotal = (Number(a.rnc_ach) || 0) + (Number(a.rec_ach) || 0);
      const assigned = targetType === 'number' ? (Number(a.assigned_number) || 0) : (Number(a.assigned_amount) || 0);
      const balance = achTotal - assigned;
      const pct = assigned > 0 ? ((achTotal / assigned) * 100) : 0;
      const deficit = pct - 100;
      const sym = (CurrencyList.find(c => c.code === (target.currency || 'INR')) || {}).symbol || '₹';
      return [
        (a.assignee && a.assignee.name) || 'N/A',
        targetType === 'number'
          ? new Intl.NumberFormat('en-IN').format(a.assigned_number || 0)
          : `${sym}${new Intl.NumberFormat('en-IN').format(a.assigned_amount || 0)}`,
        new Intl.NumberFormat('en-IN').format(a.rnc_ach || 0),
        new Intl.NumberFormat('en-IN').format(a.rec_ach || 0),
        new Intl.NumberFormat('en-IN').format(achTotal),
        targetType === 'number'
          ? new Intl.NumberFormat('en-IN').format(Math.abs(balance))
          : `${sym}${new Intl.NumberFormat('en-IN').format(Math.abs(balance))}`,
        `${deficit >= 0 ? '+' : ''}${deficit.toFixed(1)}%`
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assignees');
    const fileName = `${target.title}_Assignees_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-bullseye me-2"></i>
              {target.title} - Assignee Details
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <div className="row">
                <div className="col-md-3">
                  <strong>Period:</strong> <span className="badge bg-info">{target.period && target.period.toUpperCase()}</span>
                </div>
                <div className="col-md-3">
                  <strong>
                    {target.target_type === 'number' ? 'Total Assigned Number:' : 'Target Amount:'}
                  </strong>{' '}
                  {target.target_type === 'number'
                    ? new Intl.NumberFormat('en-IN').format(calculateTotalAssigned())
                    : `${(CurrencyList.find(c => c.code === (target.currency || 'INR')) || {}).symbol || '₹'}${new Intl.NumberFormat('en-IN').format(target.target_amount)}`}
                </div>
                <div className="col-md-3">
                  <strong>Start Date:</strong> {target.start_date && target.start_date.slice(0, 10)}
                </div>
                <div className="col-md-3">
                  <strong>End Date:</strong> {target.end_date && target.end_date.slice(0, 10)}
                </div>
              </div>
            </div>

            {safeAssignees.length === 0 && !loading ? (
              <div className="text-center py-4">
                <i className="fas fa-users fa-3x text-muted mb-3"></i>
                <p className="text-muted">No assignees found for this target</p>
              </div>
            ) : (
              <Grid
                className="kendo-table-border-block rounded-10"
                data={safeAssignees}
                loading={loading}
                style={{ height: '400px' }}
              >
                <GridColumn field="assignee.name" title="User Name" width="200px" cell={UserNameCell} />
                <GridColumn field="assigned_amount" title="Assigned Amount" width="180px" cell={AssignedAmountCell} />
                <GridColumn
                  field="rnc_ach"
                  title="RNC - Revenue From New Client"
                  headerCell={(p) => <a title="Revenue From New Client">RNC - Revenue From New Client</a>}
                  width="200px"
                  cell={RNCCell}
                />
                <GridColumn
                  field="rec_ach"
                  title="REC - Revenue From Existing Client"
                  headerCell={(p) => <a title="Revenue From Existing Client">REC - Revenue From Existing Client</a>}
                  width="220px"
                  cell={RECCell}
                />
                <GridColumn title="Total Achievement" width="170px" cell={TotalCell} />
                <GridColumn title="Balance" width="160px" cell={BalanceCell} />
                <GridColumn title="Actions" width="180px" cell={ActionCell} />
              </Grid>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-success me-2" onClick={downloadExcel}>
              <i className="fas fa-download me-2"></i>Download Excel
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-2"></i>Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
